import { Directive, ElementRef, forwardRef, Input, OnChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { getNumberFormatter } from '../utils/format.utils';

@Directive({
  selector: 'input[type=text][appNumericInput]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumericInputDirective),
      multi: true,
    },
  ],
  host: {
    '[class.active]': 'value !== null',
    '(blur)': 'handleBlur()',
    '(focus)': 'handleFocus()',
    '(input)': 'handleInput($event)',
  },
})
export class NumericInputDirective implements ControlValueAccessor, OnChanges {
  @Input() currency: string | null = null;
  @Input() locale = 'es-MX';
  @Input() maximumFractionDigits = 3;
  @Input() minimumFractionDigits = 0;
  @Input() suffix = '';
  value: number | null = null;
  private decimalSeparator = '.';
  private displayFormatter: Intl.NumberFormat;
  private editingFormatter: Intl.NumberFormat;
  private focused = false;
  private groupSeparator = ',';
  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private elementRef: ElementRef<HTMLInputElement>) {
    this.updateFormatters();
  }

  ngOnChanges(): void {
    this.updateFormatters();
    this.renderValue();
  }

  handleBlur(): void {
    this.focused = false;
    this.onTouched();
    this.renderValue();
  }

  handleFocus(): void {
    this.focused = true;
    this.renderValue();
  }

  handleInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.value = this.parseValue(input.value);
    this.onChange(this.value);
  }

  registerOnChange(onChange: (value: number | null) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(isDisabled: boolean): void {
    this.elementRef.nativeElement.disabled = isDisabled;
  }

  writeValue(value: number | null): void {
    this.value = typeof value === 'number' && Number.isFinite(value) ? value : null;
    this.renderValue();
  }

  private formatValue(value: number, editing: boolean): string {
    const formattedValue = (editing ? this.editingFormatter : this.displayFormatter).format(value);
    return `${formattedValue}${editing ? '' : this.suffix}`;
  }

  private parseValue(rawValue: string): number | null {
    if (!rawValue.trim()) {
      return null;
    }
    const normalized = rawValue
      .split(this.groupSeparator).join('')
      .replace(this.decimalSeparator, '.')
      .replace(/[^0-9.-]/g, '');
    const parsed = Number(normalized);
    if (!Number.isFinite(parsed)) {
      return null;
    }
    const precision = 10 ** this.maximumFractionDigits;
    return Math.round((parsed + Number.EPSILON) * precision) / precision;
  }

  private renderValue(): void {
    this.elementRef.nativeElement.value = this.value === null ? '' : this.formatValue(this.value, this.focused);
  }

  private updateFormatters(): void {
    this.displayFormatter = getNumberFormatter(this.locale, {
      ...(this.currency && { currency: this.currency, style: 'currency' }),
      maximumFractionDigits: this.maximumFractionDigits,
      minimumFractionDigits: this.minimumFractionDigits,
      useGrouping: true,
    });
    this.editingFormatter = getNumberFormatter(this.locale, {
      maximumFractionDigits: this.maximumFractionDigits,
      minimumFractionDigits: 0,
      useGrouping: false,
    });
    const separatorParts = getNumberFormatter(this.locale).formatToParts(12345.6);
    this.decimalSeparator = separatorParts.find((part) => part.type === 'decimal')?.value ?? '.';
    this.groupSeparator = separatorParts.find((part) => part.type === 'group')?.value ?? ',';
  }
}
